import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View } from '@olxbr/design-system'

import behaviors from '../../../util/behaviors'
import Lurker from '../../../util/lurker'
import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import tokenValidateApiCall from '../../../api/tokenValidateApiCall'
import changePasswordApiCall from '../../../api/changePasswordApiCall'
import LogoTitle from '../../molecules/LogoTitle'
import SmartLock from '../../../util/smartLock'
import SecureStorage from '../../../util/secureStorage'

import ChangePasswordBaseTemplate from '../ChangePasswordBaseTemplate'

const lurker = new Lurker('change_password')
const secureStorage = new SecureStorage('change_password')
const smartLock = new SmartLock('change_password')

// prettier-ignore
const Content = styled(View)`
  display: flex;
  flex-direction: column;
`

// prettier-ignore
const ErrorMsgWrapper = styled(View)`
  margin-top: 16px;
  margin-right: 0;
  margin-bottom: 16px;
  margin-left: 0;
`

const getPasswordExtraData = (key, password) => {
  const field = {}
  const passwordLevel = password.level

  if (passwordLevel === -1) {
    field[key] = 'sequential'
  } else if (passwordLevel === 1 || passwordLevel === 2) {
    field[key] = 'weak'
  } else if (passwordLevel === 3) {
    field[key] = 'good'
  } else if (passwordLevel === 4) {
    field[key] = 'strong'
  }
  return field
}

const errors = {
  showGenericError: false,
  showRateLimitError: false,
}

class ChangePasswordTemplate extends React.Component {
  constructor(props) {
    super(props)
    const { token } = props
    this.state = {
      ...errors,
      abTests: this.props.abTests || '',
      changePasswordToken: null,
      showCurrentPasswodInput: !token,
    }
  }

  componentDidMount() {
    lurker.pageLoadEvt()
    const { token } = this.props
    if (token) {
      this.validateToken()
    }
  }

  validateToken = () => {
    const { token } = this.props
    if (this.props.disableTokenExchange === true) {
      return
    }
    if (this.props.onTokenExchangeSubmit) {
      this.props.onTokenExchangeSubmit()
    }
    tokenValidateApiCall(token)
      .then(() => {
        this.setState({ changePasswordToken: token })
      })
      .catch(data => {
        const responseStatus = data && data.response ? data.response.status : null
        switch (responseStatus) {
          case 429:
            this.setState({ showRateLimitError: true })
            break
          case 410:
            this.props.onLinkExpired()
            break
          default:
            behaviors.goToErrorPage(`ErrorExchangingToken${responseStatus}`)
        }
      })
      .finally(() => {
        if (this.props.onTokenExchangeFinish) {
          this.props.onTokenExchangeFinish()
        }
      })
  }

  handleSubmit = ({ currentPassword, password }) => {
    this.setState({ ...errors })
    if (this.props.onSubmit) {
      this.props.onSubmit()
    }
    const { changePasswordToken, abTests } = this.state
    return changePasswordApiCall(changePasswordToken, currentPassword.value, password.value)
      .then(async data => {
        const { onSuccess, email } = this.props

        lurker.viewMessageEvt('success', {
          ...getPasswordExtraData('password_strength', password),
        })

        if (email) {
          await smartLock.saveCredential(email, password.value)
          await secureStorage.saveCredential(email, password.value)
        }

        if (onSuccess) {
          onSuccess(data)
        }
      })
      .catch(data => {
        if (this.props.onError) {
          this.props.onError()
        }

        const {
          response: { status, data: { message } = {} } = {},
          message: exceptionMessage = 'unknown',
        } = data

        behaviors.scrollToTop()

        if (status === 429) return this.handle429()
        if (status) return this.handleUnexpectedStatusCode(status, message)
        return this.handleUnexpectedError(exceptionMessage)
      })
  }

  handle429 = () => {
    lurker.viewMessageEvt('error', {
      accounts_reason: 'rate_limit_exceeded',
      accounts_status_code: 429,
    })
    this.setState({ showRateLimitError: true })
  }

  handleUnexpectedStatusCode = (status, message = 'unexpected_status_code') => {
    lurker.viewMessageEvt('error', {
      accounts_reason: message.toLowerCase(),
      accounts_status_code: status,
    })
    this.setState({ showGenericError: true })
  }

  handleUnexpectedError = (message = 'unknown') => {
    lurker.viewMessageEvt('error', {
      accounts_reason: 'unexpected_error',
      accounts_error_message: message,
    })
    this.setState({ showGenericError: true })
  }

  renderError = () => {
    const { showGenericError, showRateLimitError } = this.state
    return (
      <React.Fragment>
        {showGenericError && (
          <ErrorMsgWrapper>
            <GenericErrorBox text="Não foi possível salvar sua nova senha." />
          </ErrorMsgWrapper>
        )}
        {showRateLimitError && (
          <ErrorMsgWrapper>
            <GenericErrorBox
              showContactUs={false}
              text="Você excedeu o número de tentativas. Por favor, tente novamente mais tarde."
            />
          </ErrorMsgWrapper>
        )}
      </React.Fragment>
    )
  }

  render() {
    const { abTests } = this.props
    const { showCurrentPasswodInput } = this.state
    return (
      <Content>
        {/* prettier-ignore */}
        <LogoTitle title="Altere sua senha" />
        <ChangePasswordBaseTemplate
          lurker={lurker}
          onSubmit={this.handleSubmit}
          renderError={this.renderError}
          renderTitle="Crie uma senha longa e complexa, com letras maiúsculas, minúsculas, números e símbolos."
          showCurrentPasswodInput={showCurrentPasswodInput}
          abTests={abTests}
        />
      </Content>
    )
  }
}

ChangePasswordTemplate.propTypes = {
  disableTokenExchange: PropTypes.bool,
  onTokenExchangeSubmit: PropTypes.func,
  onTokenExchangeFinish: PropTypes.func,
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onLinkExpired: PropTypes.func,
  token: PropTypes.string,
  abTests: PropTypes.string,
}

export default ChangePasswordTemplate
